import React from 'react';
import { Link } from 'react-router-dom';
import { focusRing, radius } from '../../styles/tokens';

function Button({
  children,
  variant = 'primary',
  size = 'default',
  disabled = false,
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  onClick,
  type = 'button',
  href,
  to,
  ...props
}) {
  const baseClasses = `inline-flex items-center justify-center font-semibold ${focusRing} transition-[color,background-color,border-color,box-shadow,transform] duration-hover ease-out-strong hover:scale-[1.02] active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 disabled:active:scale-100`;

  const variantClasses = {
    primary:
      'btn-shine bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-[0_8px_24px_rgba(249,115,22,0.28)] ring-1 ring-orange-400/30 hover:from-orange-600 hover:to-orange-700 hover:shadow-glow focus-visible:ring-orange-500 ',
    secondary:
      'border border-secondary-200/70 bg-white/75 text-secondary-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] backdrop-blur-md hover:bg-white hover:shadow-soft focus-visible:ring-orange-500    ',
    ghost:
      'text-secondary-600 hover:bg-secondary-100/60 hover:text-secondary-900 focus-visible:ring-orange-500   ',
    accent:
      'btn-shine bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-[0_8px_24px_rgba(245,158,11,0.28)] hover:from-amber-600 hover:to-amber-700 hover:shadow-glow focus-visible:ring-amber-500',
    partner:
      'btn-shine bg-gradient-to-r from-orange-600 to-orange-800 text-white shadow-[0_8px_24px_rgba(154,52,18,0.28)] ring-1 ring-orange-500/30 hover:from-orange-700 hover:to-orange-900 hover:shadow-glow focus-visible:ring-orange-600',
    outline:
      'border-2 border-orange-500 text-orange-600 hover:bg-orange-50 focus-visible:ring-orange-500   ',
    danger:
      'btn-shine bg-gradient-to-r from-error-500 to-error-600 text-white shadow-[0_8px_24px_rgba(239,68,68,0.28)] ring-1 ring-error-400/30 hover:from-error-600 hover:to-error-700 hover:shadow-glow focus-visible:ring-error-500',
  };

  const sizeClasses = {
    compact: `px-3.5 py-2 text-xs sm:text-sm ${radius.sm}`,
    small: `px-3 py-1.5 text-sm ${radius.sm}`,
    default: `px-6 py-3 text-sm ${radius.md} max-lg:min-h-[44px]`,
    large: `px-8 py-4 text-base ${radius.md}`,
  };

  const iconSizes = {
    compact: 'h-3.5 w-3.5',
    small: 'h-3 w-3',
    default: 'h-4 w-4',
    large: 'h-5 w-5',
  };

  const isDisabled = disabled || loading;
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`.trim();

  const renderedIcon = (() => {
    if (loading) {
      return (
        <div
          className={`animate-spin rounded-full border-2 border-current border-t-transparent ${iconSizes[size]}`}
          aria-hidden="true"
        />
      );
    }
    if (icon) {
      const IconComponent = icon;
      return <IconComponent className={iconSizes[size]} aria-hidden="true" />;
    }
    return null;
  })();

  const content = (
    <>
      {iconPosition === 'left' && renderedIcon ? (
        <span className={children ? 'mr-2 inline-flex shrink-0' : 'inline-flex shrink-0'}>{renderedIcon}</span>
      ) : null}
      {children}
      {iconPosition === 'right' && renderedIcon ? (
        <span className={children ? 'ml-2 inline-flex shrink-0' : 'inline-flex shrink-0'}>{renderedIcon}</span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes} {...props}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      aria-busy={loading || undefined}
      className={classes}
      {...props}
    >
      {content}
    </button>
  );
}

export default Button;
